/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useMemo, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import FileUploader from '../FileUploader'
import { isImageValid } from '../FileUploader/utils'
import TextEditor from '../TextEditor'

const validationSchema = yup.object().shape({
  title: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  description: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  content: yup.string().default(''),
  category: yup.string().nullable(),
  cover: yup.mixed<File>()
    .test(
      "is-valid-format",
      "O arquivo deve ser uma imagem",
      (value: { url: string } | File | undefined | null): boolean => {
        if (value === null || value === undefined) {
          return true
        }

        if ('url' in value && typeof value?.url === 'string') {
          return true
        }

        return value instanceof File && isImageValid(value?.type)
      }
    ).nullable(),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('inactive')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormPostHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormPostHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormPost is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormPost from './src/views/PostEdit/FormPost.tsx'
 *
 * const MyComponent = () => (
 *   <FormPost />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormPost: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Título</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o título da postagem'
            name='title'
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Situação</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>Ativo</option>
            <option value='inactive'>Inativo</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Categoria</Form.Label>
          <Form.Select
            name='category'
            value={formik?.values?.category ?? ''}
            onChange={event => {
              if (event.target.value === '') {
                formik.setFieldValue('category', null)
                return
              }

              formik.setFieldValue('category', event.target.value)
            }}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          >
            <option value="">Postagem padrão</option>
            <option value='MUNDIAL_NA_MIDIA'>Mundial na mídia</option>
            <option value='IMPORTADOS_MEU_MUNDIAL'>Importados meu mundial</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Resumo</Form.Label>
          <Form.Control
            as='textarea'
            rows={4}
            placeholder='Informe o resumo da postagem'
            name='description'
            maxLength={255}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Arquivo de imagem</Form.Label>
          <FileUploader
            variant='outline-primary'
            accept="image/*"
            size='sm'
            disabled={formik.isSubmitting || isLoading}
            onReceiveFiles={([{ file }]) => {
              formik.setFieldValue('cover', file)
            }}
          >
            <FontAwesomeIcon icon={faUpload} />
            {' '}
            Enviar arquivo
          </FileUploader>
          {/* <Form.Control
            placeholder='Informe o resumo da postagem'
            name='cover'
            type='file'
            accept="image/*"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const [file] = event.currentTarget?.files ?? []
              if (file) {

                formik.setFieldValue('cover', file)
              }
            }}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          /> */}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Conteúdo</Form.Label>
          <TextEditor
            value={formik?.values?.content ?? ''}
            placeholder='Escreva aqui o conteúdo da postagem'
            readOnly={formik.isSubmitting || isLoading}
            onChange={value => formik.setFieldValue('content', value)}
          />
        </Form.Group>
        <Col>
          <Button
            variant="link"
            style={{ marginRight: 10 }}
            onClick={() => navigate('/posts')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
        {process.env.NODE_ENV === 'development' && (
          <Col md={12}>
            <pre>{JSON.stringify({
              values: formik.values,
              errors: Object.keys(formik.errors).length > 0 ? formik.errors : null,
              touched: formik.touched,
              isValid: formik.isValid,
            }, undefined, 2)}</pre>
          </Col>
        )}
      </Row>
    </Form>
  )
}

export default FormPost
